import { useCallback, useState } from 'react';

import {
	Checkbox,
	Confirm,
	Modal,
	RadioButton,
	Select,
	SpinnerOverlay,
	SubmissionButtons,
} from '@components/common';

import { useMutateConsistencyCheckRanAssignManyTemplate } from '@hooks/cmModule/consistencyCheckRan/useMutateConsistencyCheckRanAssignManyTemplate';
import { useMutateConsistencyCheckRanCheckTemplates } from '@hooks/cmModule/consistencyCheckRan/useMutateConsistencyCheckRanCheckTemplates';
import { useMutateConsistencyCheckRanUnassignManyTemplate } from '@hooks/cmModule/consistencyCheckRan/useMutateConsistencyCheckRanUnassignManyTemplate';
import useQueryConsistencyCheckRanGetAvailableTemplates from '@hooks/cmModule/consistencyCheckRan/useQueryConsistencyCheckRanGetAvailableTemplates';

import { useModal } from '@components/common/Modal/utils';

import { TConsistencyCheckRanGetAvailableTemplatesDTO } from '@interfaces/cmModule/dto/consistencyCheckDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';

import { MODAL_WIDTH_480 } from '@constants/modals';
import { ASSIGN_REFERENCE_TEMPLATE, ASSIGN_SELECT_PLACEHOLDER } from '../../../constants';
import { ASSIGN_TEXT, bulkAssignActions, UNASSIGN_TEXT } from './constants';

import styles from './BulkAssignConsistencyCheckTemplate.module.scss';

interface IBulkAssignConsistencyCheckTemplateProps {
	groupParams?: TConsistencyCheckRanGetAvailableTemplatesDTO;
	closeForm: () => void;
}

const BulkAssignConsistencyCheckTemplate = ({
	groupParams,
	closeForm,
}: IBulkAssignConsistencyCheckTemplateProps) => {
	const { onClose } = useModal();

	const [selectedType, setSelectedType] = useState(bulkAssignActions.assignTemplate);
	const [selectedOption, setSelectedOption] = useState<TSelectOption<string>>();

	const [isAllItemsInMenuSelected, setIsAllItemsInMenuSelected] = useState(false);
	const [isOpenNamesList, setIsOpenNamesList] = useState(false);
	const [isConfirm, setIsConfirm] = useState(false);

	const isAssign = selectedType === bulkAssignActions.assignTemplate;

	const {
		assignManyTemplate,
		isLoadingAssignManyTemplate,
	} = useMutateConsistencyCheckRanAssignManyTemplate({
		onSuccess: closeForm,
	});

	const {
		unassignManyTemplate,
		isLoadingUnassignManyTemplate,
	} = useMutateConsistencyCheckRanUnassignManyTemplate({
		onSuccess: closeForm,
	});

	const {
		count,
		countMismatch,
		networkElementNames,
		isLoadingCheckAvailableTemplate,
		checkAvailableTemplate,
	} = useMutateConsistencyCheckRanCheckTemplates();

	const openConfirm = useCallback(() => setIsConfirm(true), []);
	const closeConfirm = useCallback(() => setIsConfirm(false), []);

	const unassignTemplate = useCallback(() => {
		if (!groupParams) {
			return;
		}

		unassignManyTemplate(groupParams!);
	}, [groupParams, unassignManyTemplate]);

	const assignTemplate = useCallback(() => {
		if (!groupParams || !selectedOption?.id) {
			return;
		}

		assignManyTemplate({
			consistenciesAssignmentTemplateInDto: groupParams,
			goldenTemplateId: selectedOption.id.toString(),
			onlyEmpty: isAllItemsInMenuSelected,
		});
	}, [
		groupParams,
		selectedOption,
		isAllItemsInMenuSelected,
		assignManyTemplate,
	]);

	const onSubmit = useCallback(() => {
		if (isAssign) {
			assignTemplate();
		} else {
			unassignTemplate();
		}
	}, [isAssign, assignTemplate, unassignTemplate]);

	const openNamesList = useCallback(() => {
		if (!isAssign) {
			return;
		}

		setIsOpenNamesList(true);
	}, [isAssign]);

	const closeNamesList = useCallback(() => {
		setIsOpenNamesList(false);
	}, []);

	const toggleCheckbox = useCallback(() => {
		setIsAllItemsInMenuSelected((prev) => !prev);
	}, []);

	const onTypeChange = useCallback((value: string) => {
		setSelectedType(value);
	}, []);

	const {
		availableTemplates,
		isLoadingAvailableTemplates,
	} = useQueryConsistencyCheckRanGetAvailableTemplates(groupParams!);

	const onChange = useCallback((option: TSelectOption<string>) => {
		setSelectedOption(option);

		if (groupParams && option?.id) {
			checkAvailableTemplate({
				goldenTemplateId: option.id.toString(),
				consistenciesAssignmentTemplateInDto: groupParams,
			});
		}
	}, [groupParams, checkAvailableTemplate]);

	const isLoading = isLoadingAssignManyTemplate ||
		isLoadingUnassignManyTemplate ||
		isLoadingCheckAvailableTemplate;

	const isSubmitDisabled = isLoading || (isAssign && !selectedOption?.id);

	return (
		<>
			<Modal
				isOpen={isOpenNamesList}
				onClose={closeNamesList}
				title='Mismatches'
				width={MODAL_WIDTH_480}
			>
				<div className={styles.container}>
					{networkElementNames?.map((name) => (<div key={name}>{name}</div>))}
					<SubmissionButtons
						isNegativeButtonNeeded={false}
						positiveHandler={closeNamesList}
						positiveLabel='Close'
					/>
				</div>
			</Modal>
			<Modal
				isOpen={isConfirm}
				onClose={closeConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={closeConfirm}
					onSubmit={onSubmit}
					question={isAssign ? ASSIGN_TEXT : UNASSIGN_TEXT}
				/>
			</Modal>
			<div className={styles.container}>
				{isLoading && <SpinnerOverlay />}
				<RadioButton
					checked={isAssign}
					id='assign'
					label={ASSIGN_REFERENCE_TEMPLATE}
					name={bulkAssignActions.assignTemplate}
					onChange={onTypeChange}
					value={bulkAssignActions.assignTemplate}
				/>
				<div className={styles.details}>
					<Select
						disabled={!isAssign}
						handleChange={onChange}
						isLoading={isLoadingAvailableTemplates}
						isWithoutUseForms
						label='Reference template'
						maxWidth='250px'
						name='assignTemplate'
						options={availableTemplates}
						placeholder={ASSIGN_SELECT_PLACEHOLDER}
						required
						value={selectedOption}
					/>
					{!!countMismatch && (
						<div className={styles.errorMessage}>
							{countMismatch} out of {count} selected NEs do not
							support the selected template (NE type mismatch).
							These will be excluded.
							<span
								className={styles.viewBtn}
								onClick={openNamesList}
							>
								View
							</span>
						</div>
					)}
					<Checkbox
						checked={isAllItemsInMenuSelected}
						disabled={!isAssign}
						label='Assign only to empty network elements'
						name='assignOnlyEmpty'
						onChange={toggleCheckbox}
					/>
				</div>
				<RadioButton
					checked={!isAssign}
					id='unassign'
					label='Unassign reference template(s)'
					name={bulkAssignActions.unassignTemplate}
					onChange={onTypeChange}
					value={bulkAssignActions.unassignTemplate}
				/>
				<SubmissionButtons
					disabledPositive={isSubmitDisabled}
					negativeHandler={onClose}
					positiveHandler={openConfirm}
					positiveLabel='Proceed'
				/>
			</div>
		</>
	);
};

export default BulkAssignConsistencyCheckTemplate;
